import { acquireGraphToken } from '../auth/msal';
import { offlineAware } from '../api/offline';
import { appConfig } from '../config';
import {
  isRetryableStatus,
  nextRetryDelay,
  parseRetryAfter,
  sleep,
} from './retry';

export class GraphError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    /** The service asked to wait this long (ms) before trying again — the sync engine honours it. */
    public readonly retryAfterMs?: number,
  ) {
    super(message);
    this.name = 'GraphError';
  }
}

interface GraphRequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE' | 'PUT';
  body?: unknown;
  /** Raw binary body (file uploads) — mutually exclusive with `body`. */
  rawBody?: Blob;
  headers?: Record<string, string>;
  /** Extra delegated scopes for this call (incremental consent). */
  scopes?: readonly string[];
}

/**
 * Single authenticated Graph request with throttling-aware retry.
 * `path` is relative to the v1.0 base URL (or an absolute @odata.nextLink).
 */
export async function graphFetch<T>(
  path: string,
  options: GraphRequestOptions = {},
): Promise<T> {
  const url = path.startsWith('https://')
    ? path
    : `${appConfig.graphBaseUrl}${path}`;

  let waited = 0;
  let giveUpWait: number | undefined;
  for (let attempt = 0; ; attempt++) {
    const response = await offlineAware(async () => fetch(url, {
      method: options.method ?? 'GET',
      headers: {
        Authorization: `Bearer ${await acquireGraphToken(options.scopes ?? [])}`,
        Accept: 'application/json',
        ...(options.rawBody !== undefined
          ? { 'Content-Type': options.rawBody.type || 'application/octet-stream' }
          : options.body !== undefined
            ? { 'Content-Type': 'application/json' }
            : {}),
        ...options.headers,
      },
      ...(options.rawBody !== undefined
        ? { body: options.rawBody }
        : options.body !== undefined
          ? { body: JSON.stringify(options.body) }
          : {}),
    }), (options.method ?? 'GET') !== 'GET');

    if (response.ok) {
      if (response.status === 204) return undefined as T;
      return (await response.json()) as T;
    }

    if (isRetryableStatus(response.status)) {
      const retryAfter = parseRetryAfter(response.headers.get('Retry-After'));
      const delay = nextRetryDelay(attempt, retryAfter, waited);
      if (delay !== null) {
        waited += delay;
        await sleep(delay);
        continue;
      }
      if (retryAfter !== undefined) giveUpWait = retryAfter * 1000;
    }

    let code = String(response.status);
    let message = response.statusText;
    try {
      const body = (await response.json()) as {
        error?: { code?: string; message?: string };
      };
      code = body.error?.code ?? code;
      message = body.error?.message ?? message;
    } catch {
      // Non-JSON error body; keep the status text.
    }
    throw new GraphError(response.status, code, message, giveUpWait);
  }
}

/**
 * Authenticated binary download (file contents). Graph answers these with a
 * 302 to a pre-authenticated *.sharepoint.com URL, which fetch follows —
 * the CSP connect-src must therefore include the tenant SharePoint host.
 */
export async function graphFetchBlob(path: string): Promise<Blob> {
  const url = path.startsWith('https://')
    ? path
    : `${appConfig.graphBaseUrl}${path}`;
  let waited = 0;
  let giveUpWait: number | undefined;
  for (let attempt = 0; ; attempt++) {
    const response = await offlineAware(
      async () => fetch(url, { headers: { Authorization: `Bearer ${await acquireGraphToken()}` } }),
      false,
    );
    if (response.ok) return response.blob();
    if (isRetryableStatus(response.status)) {
      const retryAfter = parseRetryAfter(response.headers.get('Retry-After'));
      const delay = nextRetryDelay(attempt, retryAfter, waited);
      if (delay !== null) {
        waited += delay;
        await sleep(delay);
        continue;
      }
      if (retryAfter !== undefined) giveUpWait = retryAfter * 1000;
    }
    throw new GraphError(
      response.status,
      String(response.status),
      response.statusText,
      giveUpWait,
    );
  }
}
